import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheck, FiChevronRight } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import SectionHead from '../components/SectionHead';
import CaseStudyCard from '../components/CaseStudyCard';
import Process from '../components/Process';
import FAQ from '../components/FAQ';
import CtaBand from '../components/CtaBand';
import ProductSpotlight from '../components/ProductSpotlight';
import QuoteModal from '../components/QuoteModal';
import NotFound from './NotFound';
import usePageMeta from '../utils/usePageMeta';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, faqSchema, serviceSchema } from '../utils/schema';
import { services, caseStudies, company } from '../data/site';
import { serviceDetails } from '../data/serviceDetails';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = services.find((item) => item.id === serviceId);
  const details = serviceDetails[serviceId];
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  usePageMeta(details?.metaTitle, details?.metaDescription);

  if (!service || !details) {
    return <NotFound />;
  }

  const relatedWork = details.relatedWork
    .map((title) => caseStudies.find((project) => project.title === title))
    .filter(Boolean);

  const openModal = (planName) => {
    setSelectedPlan(planName);
    setModalOpen(true);
  };

  return (
    <>
      <JsonLd
        data={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
            { name: service.title, path: service.link }
          ]),
          serviceSchema({ name: service.title, description: details.metaDescription, path: service.link }),
          faqSchema(details.faqs)
        ]}
      />
      <header className="page-header service-page-header">
        <motion.div
          className="container"
          initial={{ y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/services">Services</Link>
            <FiChevronRight aria-hidden="true" />
            <span aria-current="page">{service.title}</span>
          </nav>
          <div className="service-icon service-page-icon">
            <Icon name={service.icon} />
          </div>
          <h1>{details.headline}</h1>
          <p>{details.intro}</p>
          <div className="btn-row">
            <button type="button" className="btn btn-lg" onClick={() => openModal('')}>
              Get a free quote <FiArrowRight />
            </button>
            <a href={company.whatsapp} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">
              <FaWhatsapp /> Chat on WhatsApp
            </a>
          </div>
        </motion.div>
      </header>

      {details.product && <ProductSpotlight productId={details.product} showFeatures={false} />}

      <section className={`section ${details.product ? 'section-alt' : ''}`}>
        <div className="container">
          <SectionHead eyebrow="What you get" title={`What's included with ${service.title}`} />
          <div className="deliverables-grid">
            {details.deliverables.map((item, index) => (
              <Reveal key={item.title} className="deliverable" delay={index * 0.04}>
                <span className="deliverable-check"><FiCheck /></span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className={`section ${details.product ? '' : 'section-alt'}`}>
        <div className="container split">
          <div>
            <SectionHead eyebrow="Who it's for" title="Built for teams like yours" />
            <ul className="audience-list">
              {details.audience.map((item) => (
                <li key={item}><FiCheck aria-hidden="true" />{item}</li>
              ))}
            </ul>
          </div>
          <Reveal className="stack-card">
            <h3>Tools & technology</h3>
            <p>We pick proven, well-supported tools so your product stays fast, secure and easy to maintain.</p>
            <ul className="tag-list">
              {details.stack.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="section" id="pricing">
        <div className="container">
          <SectionHead
            eyebrow="Pricing"
            title={details.plans ? 'Simple, transparent plans' : 'A quote built around you'}
            subtitle={details.plans
              ? 'Starting prices for typical projects. After a free consultation you get a fixed quote for your exact requirements.'
              : 'No fixed packages. Pricing depends on your institute’s size and needs.'}
            center
          />
          {details.plans ? (
            <div className="pricing-grid">
              {details.plans.map((plan, index) => (
                <Reveal key={plan.name} className={`pricing-card ${plan.popular ? 'popular' : ''}`} delay={index * 0.06}>
                  {plan.popular && <div className="popular-badge">Most Popular</div>}
                  <h3>{plan.name}</h3>
                  <div className="price">
                    {plan.price}
                    <span className="price-note">Starting price</span>
                  </div>
                  <ul className="features">
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        <FiCheck className="check-icon" aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="btn" onClick={() => openModal(plan.name)}>
                    Get a quote
                  </button>
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal className="quote-panel">
              <div>
                <h3>Priced for your institute</h3>
                <p>
                  Every institute is different. Tell us what you need and
                  we&rsquo;ll send a clear, fixed quote within one business day.
                </p>
              </div>
              <button type="button" className="btn btn-lg" onClick={() => openModal('')}>
                Get a free quote <FiArrowRight />
              </button>
            </Reveal>
          )}
        </div>
      </section>

      {relatedWork.length > 0 && (
        <section className="section section-alt">
          <div className="container">
            <div className="section-head-row">
              <SectionHead eyebrow="Related work" title="Projects like this" />
              <Link to="/work" className="text-link">
                View all projects <FiArrowRight />
              </Link>
            </div>
            <Reveal className="work-grid">
              {relatedWork.map((project) => (
                <CaseStudyCard key={project.title} project={project} />
              ))}
            </Reveal>
          </div>
        </section>
      )}

      <Process
        alt={false}
        steps={details.process}
        title={details.processTitle}
        subtitle="Exactly what happens at each stage, and what you get at the end of it."
      />

      <FAQ
        items={details.faqs}
        title={`${service.title} questions`}
      />

      <CtaBand title={`Ready to get started with ${service.title}?`} />

      <QuoteModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        serviceName={service.title}
        planName={selectedPlan}
      />
    </>
  );
};

export default ServiceDetail;
